import React, {
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Card, Space, Button, Tooltip, Select } from "antd";
import type { CardProps, TabsProps } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";

// 按钮配置接口
export interface ButtonConfig {
  key: string;
  text?: string;
  icon?: React.ReactNode;
  type?: "default" | "primary" | "dashed" | "link" | "text" | "danger";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean; // 加载状态
  hidden?: boolean;
  group?: "primary" | "secondary" | "tertiary"; // 按钮分组
  danger?: boolean; // 危险按钮样式
  style?: React.CSSProperties; // 自定义样式
  title?: string; // 悬停提示文字
  showTextOnHover?: boolean; // 是否只在悬停时显示文字，默认 false
}

// 历史记录上下文接口
interface HistoryContextType {
  saveHistory: (data: any) => void; // 保存历史记录的方法
}

// 创建历史记录上下文
const HistoryContext = createContext<HistoryContextType | null>(null);

// 导出 hook，供子组件使用
export const useContainerPanelHistory = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error(
      "useContainerPanelHistory must be used within a ContainerPanel with showHistory enabled",
    );
  }
  return context;
};

// ContainerPanel 属性接口
export interface ContainerPanelProps {
  // 标题相关
  title?: string | React.ReactNode;
  tabs?: TabsProps["items"]; // 如果提供 tabs，则使用 tabs 替代 title
  activeTabKey?: string;
  onTabChange?: (key: string) => void;

  // 按钮相关
  buttons?: ButtonConfig[] | React.ReactNode[]; // 支持配置对象或 React 节点
  /** 渲染在标题栏右侧操作区最左侧（在「删除」等按钮之前），例如角色成员「全选」 */
  extraLeading?: React.ReactNode;

  // 内容
  children: React.ReactNode;

  // 样式相关
  height?: number | string; // 高度，不设置则自适应内容
  contentPadding?: number | string; // 内容内边距，默认 16px
  bordered?: boolean; // 是否有边框，默认 true
  shadow?: boolean; // 是否有阴影，默认 false

  // Card 其他属性
  cardProps?: CardProps;

  // 标题样式相关
  headerPadding?: string | number;

  // 收拢功能相关
  collapsible?: boolean; // 是否启用收拢功能，默认 false
  defaultCollapsed?: boolean; // 默认是否收拢，默认 false
  onCollapseChange?: (collapsed: boolean) => void; // 收拢状态变化回调

  // 标题点击回调
  onTitleClick?: () => void;

  // 历史记录相关
  showHistory?: boolean; // 是否显示历史记录下拉框，默认 false
  onHistorySelect?: (data: any) => void; // 选择历史记录时的回调
  maxHistoryCount?: number; // 最大历史记录数量，默认 50
}

/**
 * 通用面板容器组件
 * 支持标题或标签页，支持右侧按钮分组，支持边框和阴影
 */
const ContainerPanel: React.FC<ContainerPanelProps> = ({
  title,
  tabs,
  activeTabKey,
  onTabChange,
  buttons = [],
  extraLeading,
  children,
  height,
  contentPadding = 16,
  bordered = true,
  shadow = false,
  cardProps = {},
  headerPadding,
  collapsible = false,
  defaultCollapsed = false,
  onCollapseChange,
  onTitleClick,
  showHistory = false,
  onHistorySelect,
  maxHistoryCount = 50,
}) => {
  // 收拢状态
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // 历史记录状态
  const [historyList, setHistoryList] = useState<any[]>([]);

  // 切换收拢状态
  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onCollapseChange?.(newCollapsed);
  };

  // 保存历史记录
  const saveHistory = useCallback(
    (data: any) => {
      if (!showHistory) return;

      try {
        // 将数据转换为 JSON 字符串进行存储
        const jsonData = typeof data === "string" ? data : JSON.stringify(data);

        setHistoryList((prev) => {
          // 避免重复添加相同的历史记录
          const newList = [
            jsonData,
            ...prev.filter((item) => item !== jsonData),
          ];
          // 限制历史记录数量
          return newList.slice(0, maxHistoryCount);
        });
      } catch (error) {
        console.error("保存历史记录失败:", error);
      }
    },
    [showHistory, maxHistoryCount],
  );

  // 选择历史记录
  const handleHistorySelect = (value: string) => {
    try {
      // 尝试解析 JSON，如果失败则直接使用字符串
      let parsedData: any;
      try {
        parsedData = JSON.parse(value);
      } catch {
        parsedData = value;
      }
      onHistorySelect?.(parsedData);
    } catch (error) {
      console.error("解析历史记录失败:", error);
    }
  };

  // 历史记录上下文值
  const historyContextValue: HistoryContextType = useMemo(
    () => ({
      saveHistory,
    }),
    [saveHistory],
  );
  // 渲染按钮组
  const renderButtonGroup = (
    groupType: "primary" | "secondary" | "tertiary",
  ): React.ReactNode => {
    // 过滤出该分组的按钮
    const groupButtons = (buttons as ButtonConfig[]).filter(
      (button) =>
        typeof button === "object" &&
        "group" in button &&
        button.group === groupType,
    );

    if (groupButtons.length === 0) return null;

    return (
      <Space>
        {groupButtons.map((button) => {
          if (
            typeof button === "object" &&
            "hidden" in button &&
            button.hidden
          ) {
            return null;
          }

          if (typeof button === "object" && "key" in button) {
            const buttonType =
              button.type === "danger" ? "default" : button.type;
            const buttonContent = (
              <Button
                type={buttonType}
                danger={button.type === "danger" || button.danger}
                icon={button.icon}
                disabled={button.disabled}
                loading={button.loading}
                onClick={button.onClick}
                size="small"
                style={button.style}
              >
                {button.showTextOnHover ? null : button.text}
              </Button>
            );

            // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
            if (button.showTextOnHover || button.title) {
              const tooltipTitle = button.title || button.text || "";
              return (
                <Tooltip key={button.key} title={tooltipTitle}>
                  {buttonContent}
                </Tooltip>
              );
            }

            return (
              <React.Fragment key={button.key}>{buttonContent}</React.Fragment>
            );
          }

          return null;
        })}
      </Space>
    );
  };

  // 渲染历史记录下拉框
  const renderHistorySelect = (): React.ReactNode => {
    if (!showHistory || historyList.length === 0) {
      return null;
    }

    return (
      <Select
        placeholder="历史记录"
        style={{ width: 150, marginRight: 8 }}
        size="small"
        value={undefined}
        onChange={handleHistorySelect}
        options={historyList.map((item, index) => {
          // 尝试解析 JSON 以生成更友好的显示文本
          let displayText: string;
          try {
            const parsed = JSON.parse(item);
            // 如果解析成功，尝试生成简短描述
            if (typeof parsed === "object" && parsed !== null) {
              displayText = `记录 ${index + 1}`;
            } else {
              displayText = String(parsed).substring(0, 20);
            }
          } catch {
            displayText = String(item).substring(0, 20);
          }

          return {
            label: displayText,
            value: item,
          };
        })}
      />
    );
  };

  // 渲染所有按钮
  const renderButtons = (): React.ReactNode => {
    // 分离 ButtonConfig 和 ReactNode
    const buttonConfigs: ButtonConfig[] = [];
    const reactNodes: React.ReactNode[] = [];

    // 如果有传入的按钮，处理它们
    if (Array.isArray(buttons) && buttons.length > 0) {
      buttons.forEach((btn) => {
        if (React.isValidElement(btn)) {
          reactNodes.push(btn);
        } else if (typeof btn === "object" && btn !== null) {
          buttonConfigs.push(btn as ButtonConfig);
        }
      });
    }

    // 如果有收拢功能，将收拢按钮添加到 secondary 组（第二组，在 primary 组左侧）
    if (collapsible) {
      buttonConfigs.push({
        key: "collapse",
        icon: collapsed ? <DownOutlined /> : <UpOutlined />,
        type: "text",
        group: "secondary",
        onClick: handleToggleCollapse,
        title: collapsed ? "展开" : "收拢",
        showTextOnHover: true,
        style: {
          padding: 0,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      });
    }

    // 如果没有按钮且没有收拢功能，返回 null
    if (buttonConfigs.length === 0 && reactNodes.length === 0 && !collapsible) {
      return null;
    }

    // 如果只有 React 节点，直接渲染
    if (buttonConfigs.length === 0 && reactNodes.length > 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            justifyContent: "flex-end",
          }}
        >
          <Space>{reactNodes}</Space>
        </div>
      );
    }

    // 处理按钮配置
    const primaryButtons = buttonConfigs.filter(
      (btn) => "group" in btn && btn.group === "primary",
    );
    const secondaryButtons = buttonConfigs.filter(
      (btn) => "group" in btn && btn.group === "secondary",
    );
    const tertiaryButtons = buttonConfigs.filter(
      (btn) => "group" in btn && btn.group === "tertiary",
    );
    const ungroupedButtons = buttonConfigs.filter(
      (btn) => !("group" in btn) || !btn.group,
    );

    // 判断是否有左侧按钮（tertiary）
    const hasTertiaryButtons = tertiaryButtons.length > 0;
    // 判断是否有右侧按钮组（primary 或 secondary）
    const hasRightButtonGroups =
      primaryButtons.length > 0 || secondaryButtons.length > 0;
    // 判断是否需要靠右对齐：如果没有左侧按钮，只有右侧按钮或未分组按钮，则整体靠右
    const shouldAlignRight =
      !hasTertiaryButtons &&
      (hasRightButtonGroups ||
        ungroupedButtons.length > 0 ||
        reactNodes.length > 0);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          justifyContent: shouldAlignRight ? "flex-end" : "flex-start",
        }}
      >
        {extraLeading}
        {/* 历史记录下拉框 - 显示在标题右侧，按钮左侧 */}
        {renderHistorySelect()}

        {/* 第三分组 - 最左侧 */}
        {tertiaryButtons.length > 0 && renderButtonGroup("tertiary")}

        {/* 主分组和次分组的容器 - 在右侧 */}
        {(primaryButtons.length > 0 || secondaryButtons.length > 0) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px", // 组间间距：16px，大于组内按钮间距（8px），以区分不同分组
              marginLeft: hasTertiaryButtons ? "auto" : undefined,
            }}
          >
            {/* 次分组 - 第二组（在 primary 左侧） */}
            {secondaryButtons.length > 0 && renderButtonGroup("secondary")}

            {/* 主分组 - 第一组（最右侧） */}
            {primaryButtons.length > 0 && renderButtonGroup("primary")}
          </div>
        )}

        {/* 未分组的按钮配置 */}
        {ungroupedButtons.length > 0 && (
          <Space>
            {ungroupedButtons.map((button) => {
              if (
                typeof button === "object" &&
                "hidden" in button &&
                button.hidden
              ) {
                return null;
              }

              if (typeof button === "object" && "key" in button) {
                const buttonType =
                  button.type === "danger" ? "default" : button.type;
                const buttonContent = (
                  <Button
                    type={buttonType}
                    danger={button.type === "danger"}
                    icon={button.icon}
                    disabled={button.disabled}
                    onClick={button.onClick}
                    size="small"
                    style={button.style}
                  >
                    {button.showTextOnHover ? null : button.text}
                  </Button>
                );

                // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
                if (button.showTextOnHover || button.title) {
                  const tooltipTitle = button.title || button.text || "";
                  return (
                    <Tooltip key={button.key} title={tooltipTitle}>
                      {buttonContent}
                    </Tooltip>
                  );
                }

                return (
                  <React.Fragment key={button.key}>
                    {buttonContent}
                  </React.Fragment>
                );
              }

              return null;
            })}
          </Space>
        )}

        {/* React 节点按钮 */}
        {reactNodes.length > 0 && <Space>{reactNodes}</Space>}
      </div>
    );
  };

  // 处理标题点击事件
  const renderTitle = (): React.ReactNode => {
    if (title && onTitleClick) {
      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onTitleClick();
        }
      };

      return (
        <button
          type="button"
          onClick={onTitleClick}
          onKeyDown={handleKeyDown}
          style={{
            cursor: "pointer",
            userSelect: "none",
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            font: "inherit",
            color: "inherit",
            textAlign: "inherit",
          }}
        >
          {title}
        </button>
      );
    }

    return title;
  };

  const cardTabList = tabs?.map((item) => ({
    key: String(item.key),
    label: item.label,
  }));

  // tabs 模式下，内容区渲染当前选中 tab 的 children；若 tab 无 children 则使用传入的 children
  const bodyContent = tabs
    ? (tabs.find((t) => t.key === activeTabKey)?.children ?? children)
    : children;

  let cardHeight: string;
  if (height === undefined) {
    cardHeight = "100%";
  } else if (height === "auto") {
    cardHeight = "auto";
  } else if (typeof height === "number") {
    cardHeight = `${height}px`;
  } else {
    cardHeight = height;
  }

  // 构建 Card 样式
  const cardStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    border: bordered ? COMPONENT_OUTLINE_BORDER : "none",
    ...(shadow && {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    }),
    ...cardProps.style,
    height: cardHeight,
  };

  // 构建 Card body 样式：height="auto" 时随内容自适应；有固定 height 时限制 body 不溢出；否则 flex 填满
  const isAutoHeight = height === "auto";
  const bodyStyle: React.CSSProperties = {
    padding:
      typeof contentPadding === "number"
        ? `${contentPadding}px`
        : contentPadding,
    ...(isAutoHeight
      ? { height: "auto", minHeight: "auto" }
      : { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }),
    ...(height !== undefined && height !== "auto" && { overflow: "hidden" }),
    ...cardProps.styles?.body,
  };

  // 构建 Card header 样式（标题栏下划线最后设置，避免被 cardProps 覆盖）
  const headerStyle: React.CSSProperties = {
    ...(headerPadding !== undefined ? { padding: headerPadding } : undefined),
    ...cardProps.styles?.header,
    borderBottom: COMPONENT_OUTLINE_BORDER,
  };

  const panelClassName = [
    cardProps.className,
    tabs ? "container-panel-with-header-tabs" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {tabs ? (
        <style>
          {`
            .container-panel-with-header-tabs.ant-card-contain-tabs > .ant-card-head {
              padding: 0 16px;
            }
            .container-panel-with-header-tabs .ant-card-head-wrapper:empty {
              display: none;
            }
            .container-panel-with-header-tabs .ant-card-head-tabs .ant-tabs-nav {
              margin-bottom: 0;
            }
            .container-panel-with-header-tabs .ant-card-head-tabs .ant-tabs-nav::before {
              display: none;
            }
          `}
        </style>
      ) : null}
      <HistoryContext.Provider value={historyContextValue}>
        <Card
          {...cardProps}
          className={panelClassName || undefined}
          title={tabs ? undefined : renderTitle()}
          extra={tabs ? undefined : renderButtons()}
          tabList={cardTabList}
          activeTabKey={tabs ? activeTabKey : undefined}
          onTabChange={tabs ? onTabChange : undefined}
          tabBarExtraContent={tabs ? renderButtons() : undefined}
          tabProps={
            tabs
              ? {
                  tabBarGutter: 32,
                  indicator: { size: 2 },
                  ...cardProps.tabProps,
                }
              : cardProps.tabProps
          }
          style={cardStyle}
          styles={{
            ...cardProps.styles,
            body: bodyStyle,
            header: headerStyle,
          }}
        >
          {!collapsed && bodyContent}
        </Card>
      </HistoryContext.Provider>
    </>
  );
};

export default ContainerPanel;
